import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  DateRange,
  extractDateRange,
  extractNumber,
  HxCurrencyPipe,
  HxFixedPipe,
  HxMoneyFormatPipe,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  StatisticsClientSalesModel,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { subDays, subMonths } from 'date-fns';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
    selector: 'app-statistics-client',
    templateUrl: './client-sales.component.html',
    styleUrls: ['./client-sales.component.css'],
    imports: [HxDateRangeSelectComponent, FormsModule, HxStoreSelectComponent, NgIf, NgFor, NgClass, RouterLink, HxLoaderComponent, NgbPagination, TranslocoPipe, HxCurrencyPipe, HxUiLabelPipe, HxFixedPipe, HxMoneyFormatPipe]
})
export class ClientSalesComponent implements OnInit, OnDestroy {
  sales: StatisticsClientSalesModel[] = [];
  isloading = {
    sales: false,
  };
  params: {
    page: number,
    limit: number,
    date?: DateRange,
    storeId?: number,
    query?: string,
  }= { page: 1, limit: 20 };
  pagination = {
    allItemCount: 0
  };
  private timeout: NodeJS.Timeout;
  private sub!: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.sub = this.aRoute.queryParamMap.subscribe(async paramMap => {
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(subDays(subMonths(new Date(), 1), 1)), to: isoDate(subDays(new Date(), 1))};
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.query = paramMap.get('query');
      this.params.storeId = extractNumber('storeId', paramMap);
      this.loadClientSales();
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  applyFilters() {
    this.navigate();
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  onStoreChanged(store: StoreBasicModel | StoreBasicModel[] | undefined) {
    if (!Array.isArray(store)){
      this.params.storeId = store?.id;
      this.applyFilters();
    }
  }

  onQueryChanged() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      this.applyFilters();
    }, 800);
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private async loadClientSales() {
    this.sales = [];
    this.isloading.sales = true;
    try {
      const sales = await this.reportService.getStatisticsClientSales({fromDate: this.params.date.from, toDate: this.params.date.to, page: this.params.page, limit: this.params.limit, query: this.params.query, storeIds: [this.params.storeId] });
      if (sales.count > 0) {
        this.sales = sales.list;
        this.pagination.allItemCount = sales.count;
      } else {
        this.sales = [];
      }
    } finally {
      this.isloading.sales = false;
    }
  }
}
